import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { ChevronDown, Clipboard, Code, Download, HelpCircle, Images, Loader2, Sparkles, Upload, X } from "lucide-react";
import { type FormEvent, useCallback, useMemo, useState } from "react";

import AppLogoIcon from "@/components/app-logo-icon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
interface SocialImageVariation {
  id: string;
  download_url: string;
  filename: string;
  custom_prompt?: string;
  is_ai_generated: boolean;
}

interface SocialImageBase {
  id: string;
  title?: string;
  description?: string;
  url: string;
  style?: string;
  customer_email?: string;
  background_color?: string;
  text_color?: string;
  current_logo_url?: string | null;
  current_custom_image_url?: string | null;
  current_blog_author_avatar_url?: string | null;
  images: {
    id: string;
    download_url: string;
    filename: string;
    is_ai_generated: boolean;
  }[];
}

type SocialImage = SocialImageBase &
  (
    | {
        page_type: "ecommerce_product";
        unique_selling_point: string;
      }
    | {
        page_type: "podcast_episode";
        episode_date: string;
      }
    | {
        page_type: "event_page";
        event_date: string | null;
        event_location: string | null;
      }
    | {
        page_type: "blog_post";
        blog_author: string;
        blog_date: string;
        blog_author_avatar_url?: string | null;
      }
    | {
        page_type: "book";
        book_author: string;
        book_series_number: number;
      }
    | { page_type: string }
  );

interface ShowProps {
  social_image: SocialImage;
}

export default function Show({ social_image }: ShowProps) {
  const { design_styles = [] } = usePage().props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<SocialImageVariation | null>(null);

  // Collapsible section state (accordion behavior - only one open at a time)
  const [openSection, setOpenSection] = useState<"textColors" | "logo" | "image" | null>("textColors");

  // Extract page-specific values based on page_type
  const getPageSpecificDefaults = () => {
    const pageType = social_image.page_type;
    const defaults: Record<string, string | number | null> = {};

    if (pageType === "ecommerce_product" && "unique_selling_point" in social_image) {
      defaults.unique_selling_point = social_image.unique_selling_point ?? "";
    } else if (pageType === "podcast_episode" && "episode_date" in social_image) {
      defaults.episode_date = social_image.episode_date ?? "";
    } else if (pageType === "event_page" && "event_date" in social_image) {
      defaults.event_date = social_image.event_date ?? "";
      defaults.event_location = social_image.event_location ?? "";
    } else if (pageType === "blog_post" && "blog_author" in social_image) {
      defaults.blog_author = social_image.blog_author ?? "";
      defaults.blog_date = social_image.blog_date ?? "";
    } else if (pageType === "book" && "book_author" in social_image) {
      defaults.book_author = social_image.book_author ?? "";
      defaults.book_series_number = social_image.book_series_number ?? null;
    }

    return defaults;
  };

  const pageSpecificDefaults = getPageSpecificDefaults();

  const {
    data: templateFormData,
    setData: setTemplateFormData,
    post: postTemplateFormData,
    processing: processingTemplateRefinement,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    reset: resetTemplateFormData,
  } = useForm({
    title: social_image.title ?? "",
    description: social_image.description ?? "",
    background_color: social_image.background_color ?? "#FFFFFF",
    text_color: social_image.text_color ?? "#000000",
    custom_image: null as File | null,
    custom_image_url: "",
    custom_image_name: "",
    custom_image_preview: null as string | null,
    remove_custom_image: false,
    custom_logo: null as File | null,
    custom_logo_url: "",
    custom_logo_name: "",
    custom_logo_preview: null as string | null,
    remove_custom_logo: false,
    base_social_image_id: "",
    // Page-specific fields
    unique_selling_point: (pageSpecificDefaults.unique_selling_point as string) ?? "",
    episode_date: (pageSpecificDefaults.episode_date as string) ?? "",
    event_date: (pageSpecificDefaults.event_date as string) ?? "",
    event_location: (pageSpecificDefaults.event_location as string) ?? "",
    blog_author: (pageSpecificDefaults.blog_author as string) ?? "",
    blog_date: (pageSpecificDefaults.blog_date as string) ?? "",
    blog_author_avatar: null as File | null,
    blog_author_avatar_url: "",
    blog_author_avatar_name: "",
    blog_author_avatar_preview: null as string | null,
    book_author: (pageSpecificDefaults.book_author as string) ?? "",
    book_series_number: pageSpecificDefaults.book_series_number as number | null,
  });

  const {
    data: aiImageFormData,
    setData: setAiImageFormData,
    post: postAiImageFormData,
    processing: processingAiImageFormData,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    reset: resetAiImageFormData,
    errors: aiImageFormErrors,
  } = useForm({
    style: social_image.style ?? design_styles[0]?.value,
    image_url: "",
    session_id: social_image.id,
    custom_prompt: "",
    include_screenshot: false,
    base_social_image_id: "",
  });

  const variations = useMemo(
    () => social_image.images.filter((image) => !image.filename.includes("screenshot")),
    [social_image.images],
  );

  const isSingular = variations.length === 1;
  const currentLogoUrl = social_image.current_logo_url?.trim() ?? "";
  const currentCustomImageUrl = social_image.current_custom_image_url?.trim() ?? "";
  const hasNewLogo =
    Boolean(templateFormData.custom_logo_name) ||
    Boolean(templateFormData.custom_logo_preview) ||
    templateFormData.custom_logo_url.trim() !== "";
  const hasNewImage =
    Boolean(templateFormData.custom_image_name) ||
    Boolean(templateFormData.custom_image_preview) ||
    templateFormData.custom_image_url.trim() !== "";
  const showCurrentLogo = currentLogoUrl !== "" && !templateFormData.remove_custom_logo && !hasNewLogo;
  const showCurrentImage = currentCustomImageUrl !== "" && !templateFormData.remove_custom_image && !hasNewImage;

  const openRefinementDialog = (image: SocialImageVariation) => {
    setSelectedImage(image);
    if (image.is_ai_generated) {
      setAiImageFormData("image_url", image.download_url);
      setAiImageFormData("style", social_image.style ?? "default");
      setAiImageFormData("base_social_image_id", social_image.id);
    } else {
      // Initialize template form with current social image values
      setTemplateFormData("title", social_image.title ?? "");
      setTemplateFormData("description", social_image.description ?? "");
      setTemplateFormData("base_social_image_id", social_image.id);
      setTemplateFormData("background_color", social_image.background_color ?? "#FFFFFF");
      setTemplateFormData("text_color", social_image.text_color ?? "#000000");
      setTemplateFormData("custom_image", null);
      setTemplateFormData("custom_image_url", "");
      setTemplateFormData("custom_image_name", "");
      setTemplateFormData("custom_image_preview", null);
      setTemplateFormData("remove_custom_image", false);
      setTemplateFormData("custom_logo", null);
      setTemplateFormData("custom_logo_url", "");
      setTemplateFormData("custom_logo_name", "");
      setTemplateFormData("custom_logo_preview", null);
      setTemplateFormData("remove_custom_logo", false);

      // Initialize page-specific fields
      const defaults = getPageSpecificDefaults();
      if (social_image.page_type === "ecommerce_product") {
        setTemplateFormData("unique_selling_point", (defaults.unique_selling_point as string) ?? "");
      } else if (social_image.page_type === "podcast_episode") {
        setTemplateFormData("episode_date", (defaults.episode_date as string) ?? "");
      } else if (social_image.page_type === "event_page") {
        setTemplateFormData("event_date", (defaults.event_date as string) ?? "");
        setTemplateFormData("event_location", (defaults.event_location as string) ?? "");
      } else if (social_image.page_type === "blog_post") {
        setTemplateFormData("blog_author", (defaults.blog_author as string) ?? "");
        setTemplateFormData("blog_date", (defaults.blog_date as string) ?? "");
        setTemplateFormData("blog_author_avatar", null);
        setTemplateFormData("blog_author_avatar_url", "");
        setTemplateFormData("blog_author_avatar_name", "");
        setTemplateFormData("blog_author_avatar_preview", null);
      } else if (social_image.page_type === "book") {
        setTemplateFormData("book_author", (defaults.book_author as string) ?? "");
        setTemplateFormData("book_series_number", defaults.book_series_number as number | null);
      }
    }
    setDialogOpen(true);
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setSelectedImage(null);
      resetAiImageFormData();
      resetTemplateFormData();
    }
  };

  const handleTemplateRefinementSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    postTemplateFormData("/generate/template-refinement", {
      preserveScroll: true,
      onSuccess: () => {
        resetTemplateFormData();
        setDialogOpen(false);
      },
    });
  };

  const handleAiImageRefinementSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    postAiImageFormData("/generate", {
      preserveScroll: true,
      onSuccess: () => {
        resetAiImageFormData();
        setDialogOpen(false);
      },
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTemplateFormData("custom_image", file);
      setTemplateFormData("custom_image_name", file.name);
      setTemplateFormData("custom_image_url", ""); // Clear URL if file is uploaded
      setTemplateFormData("remove_custom_image", false);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateFormData("custom_image_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setTemplateFormData("custom_image", null);
    setTemplateFormData("custom_image_name", "");
    setTemplateFormData("custom_image_preview", null);
    setTemplateFormData("custom_image_url", "");
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTemplateFormData("custom_logo", file);
      setTemplateFormData("custom_logo_name", file.name);
      setTemplateFormData("custom_logo_url", ""); // Clear URL if file is uploaded
      setTemplateFormData("remove_custom_logo", false);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateFormData("custom_logo_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setTemplateFormData("custom_logo", null);
    setTemplateFormData("custom_logo_name", "");
    setTemplateFormData("custom_logo_preview", null);
    setTemplateFormData("custom_logo_url", "");
  };

  const handleRemoveCurrentLogo = () => {
    setTemplateFormData("custom_logo", null);
    setTemplateFormData("custom_logo_url", "");
    setTemplateFormData("custom_logo_name", "");
    setTemplateFormData("custom_logo_preview", null);
    setTemplateFormData("remove_custom_logo", true);
  };

  const handleRemoveCurrentImage = () => {
    setTemplateFormData("custom_image", null);
    setTemplateFormData("custom_image_url", "");
    setTemplateFormData("custom_image_name", "");
    setTemplateFormData("custom_image_preview", null);
    setTemplateFormData("remove_custom_image", true);
  };

  const handleLogoPaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          setTemplateFormData("custom_logo", file);
          setTemplateFormData("custom_logo_name", `pasted-logo.${file.type.split("/")[1] || "png"}`);
          setTemplateFormData("custom_logo_url", "");
          setTemplateFormData("remove_custom_logo", false);
          const reader = new FileReader();
          reader.onloadend = () => {
            setTemplateFormData("custom_logo_preview", reader.result as string);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, []);

  const handleImagePaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          setTemplateFormData("custom_image", file);
          setTemplateFormData("custom_image_name", `pasted-image.${file.type.split("/")[1] || "png"}`);
          setTemplateFormData("custom_image_url", "");
          setTemplateFormData("remove_custom_image", false);
          const reader = new FileReader();
          reader.onloadend = () => {
            setTemplateFormData("custom_image_preview", reader.result as string);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, []);

  const handleAuthorAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTemplateFormData("blog_author_avatar", file);
      setTemplateFormData("blog_author_avatar_name", file.name);
      setTemplateFormData("blog_author_avatar_url", "");
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateFormData("blog_author_avatar_preview", reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAuthorAvatar = () => {
    setTemplateFormData("blog_author_avatar", null);
    setTemplateFormData("blog_author_avatar_name", "");
    setTemplateFormData("blog_author_avatar_preview", null);
    setTemplateFormData("blog_author_avatar_url", "");
  };

  const handleAuthorAvatarPaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          setTemplateFormData("blog_author_avatar", file);
          setTemplateFormData("blog_author_avatar_name", `pasted-avatar.${file.type.split("/")[1] || "png"}`);
          setTemplateFormData("blog_author_avatar_url", "");
          const reader = new FileReader();
          reader.onloadend = () => {
            setTemplateFormData("blog_author_avatar_preview", reader.result as string);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, []);

  // Computed values for blog author avatar
  const currentBlogAuthorAvatarUrl = social_image.current_blog_author_avatar_url?.trim() ?? "";
  const hasNewAuthorAvatar =
    Boolean(templateFormData.blog_author_avatar_name) ||
    Boolean(templateFormData.blog_author_avatar_preview) ||
    templateFormData.blog_author_avatar_url.trim() !== "";
  const showCurrentAuthorAvatar = currentBlogAuthorAvatarUrl !== "" && !hasNewAuthorAvatar;

  return (
    <>
      <Head title={isSingular ? "Your Page Preview Is Ready" : "Your Page Previews Are Ready"} />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto max-w-7xl px-4 py-12">
          {/* Header */}
          <header className="mb-12 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-green-500">
              <AppLogoIcon className="h-6 w-6 text-white" />
            </div>
            <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">
              {isSingular ? "Your Page Preview Is Ready!" : "Your Page Previews Are Ready!"}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              {isSingular
                ? "Here is your professionally designed variation"
                : "Here are your professionally designed variations"}
            </p>
          </header>

          {/* Images Grid */}
          <div className="mb-12 flex flex-wrap justify-center gap-8">
            {variations.map((image, index) => (
              <div
                key={image.id}
                className="group max-w-[600px] overflow-hidden rounded-lg bg-white shadow-lg transition-transform hover:-translate-y-1 dark:bg-gray-800"
              >
                <div className="relative max-h-[315px] overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={image.download_url}
                    alt={`${social_image.title} - Variation ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                  {/* Badge in top-left corner */}
                  {image.is_ai_generated ? (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-purple-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
                      <Sparkles className="h-3.5 w-3.5" />
                      AI Generated
                    </div>
                  ) : (
                    <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg">
                      <Code className="h-3.5 w-3.5" />
                      Template
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => openRefinementDialog(image)}
                      className="pointer-events-auto inline-flex cursor-pointer items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-xs font-semibold tracking-wide text-white uppercase opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:bg-black/70 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:outline-none"
                      aria-label="Refine this variation"
                    >
                      <Images className="h-4 w-4" />
                      Refine
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {/* Variation label with description */}
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {image.is_ai_generated ? "AI Variation" : "Template Variation"}
                    </h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {image.is_ai_generated ? "Creative & unique" : "Editable fields"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <a
                      href={image.download_url}
                      download={image.filename}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-orange-500 bg-transparent px-3 py-2 text-sm font-medium text-orange-600 transition-colors hover:bg-orange-50 dark:hover:bg-gray-900"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* How to Use Section */}
          <div className="mb-16 rounded-2xl bg-gradient-to-br from-orange-50 to-red-50 p-8 dark:from-gray-800 dark:to-gray-900">
            <div className="mx-auto max-w-4xl">
              <div className="mb-6 text-center">
                <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
                  How to Use Your New Social Image
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  Watch this quick tutorial to learn how to add {isSingular ? "this image" : "these images"} to your
                  website
                </p>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30">
                    <span className="text-lg font-bold">1</span>
                  </div>
                  <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">Download Image</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isSingular
                      ? "Download the image and upload it to your server or CDN to use it in your meta tags"
                      : "Choose your favorite variation and download the image and upload it to your server or CDN to use it in your meta tags"}
                  </p>
                </div>

                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30">
                    <span className="text-lg font-bold">2</span>
                  </div>
                  <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">Update Your Meta Tags</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Update the Open Graph meta tags from your HTML head to use the new image URL
                  </p>
                </div>

                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30">
                    <span className="text-lg font-bold">3</span>
                  </div>
                  <h3 className="mb-1 font-semibold text-gray-900 dark:text-white">Share and Engage</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Your page now shows a beautiful preview on social media
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-amber-600 dark:text-amber-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      <p className="mb-2">
                        <span className="font-semibold">Important:</span> Social networks cache preview images. After
                        updating your meta tags:
                      </p>
                      <ul className="ml-4 list-disc space-y-1 text-gray-600 dark:text-gray-400">
                        <li>
                          Use{" "}
                          <a
                            href="https://developers.facebook.com/tools/debug/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:underline"
                          >
                            Facebook&apos;s Sharing Debugger
                          </a>{" "}
                          or{" "}
                          <a
                            href="https://cards-dev.twitter.com/validator"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:underline"
                          >
                            X&apos;s Card Validator
                          </a>{" "}
                          to clear the cache
                        </li>
                        <li>Even after clearing, it may take up to 24 hours for the new image to appear</li>
                        <li>New shares will show the updated image immediately</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="border-t border-gray-200 pt-8 text-center dark:border-gray-700">
            <div className="mb-4 flex items-center justify-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500">
                <AppLogoIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-orange-600">OG Pilot</span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isSingular ? "Need more variations? " : "Need more variations? "}
              <Link href="/" className="text-orange-600 hover:text-orange-700">
                Generate another set
              </Link>
            </p>
          </footer>
        </div>
      </div>
      <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col lg:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Refine this variation</DialogTitle>
            <DialogDescription>
              {selectedImage?.is_ai_generated
                ? "Generate 2 fresh variations starting from the selected image and optionally switch up the design style before you run it."
                : "Generate a new variation of the social preview."}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {selectedImage && (
              <div className="mx-auto mb-4 max-h-[315px] max-w-[600px] overflow-hidden rounded-lg border border-orange-100 dark:border-orange-900/40">
                <img
                  src={selectedImage.download_url}
                  alt="Selected variation for refinement"
                  className="h-full w-full object-cover"
                />
              </div>
            )}

            {selectedImage?.is_ai_generated ? (
              <form id="ai-refinement-form" onSubmit={handleAiImageRefinementSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="refinement-style">Choose a style</Label>
                  <Select
                    value={aiImageFormData.style}
                    onValueChange={(value) => setAiImageFormData("style", value)}
                    aria-label="Refinement style"
                  >
                    <SelectTrigger id="refinement-style">
                      <SelectValue placeholder="Select a style" />
                    </SelectTrigger>
                    <SelectContent>
                      {design_styles.map((styleOption) => (
                        <SelectItem key={styleOption.value} value={styleOption.value}>
                          {styleOption.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-start gap-2 space-y-2">
                  <Checkbox
                    id="include-screenshot"
                    checked={!!aiImageFormData.include_screenshot}
                    onCheckedChange={(checked) => setAiImageFormData("include_screenshot", Boolean(checked))}
                  />
                  <div className="grid gap-2">
                    <Label htmlFor="include-screenshot">Include webpage screenshot</Label>
                    <p className="text-muted-foreground text-sm">
                      This helps produce better results when the image is not great looking.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="refinement-custom-prompt">Prompt (optional)</Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Describe the social image you want to create. You can specify logo placement if it&apos;s present on
                    the URL like &apos;top-left&apos;, &apos;top-right&apos;, &apos;bottom-right&apos; corner, the color
                    palette you want to use for the background image or text, use other certain elements from your page
                    URL. Leave blank and we&apos;ll create an automatic image based on your selected style and URL data.
                  </p>
                  <Input
                    id="refinement-custom-prompt"
                    value={aiImageFormData.custom_prompt}
                    placeholder="Enter a custom prompt for the social image"
                    onChange={(e) => setAiImageFormData("custom_prompt", e.target.value)}
                  />
                </div>
                {aiImageFormErrors.style && (
                  <p className="text-sm text-red-500" role="alert">
                    {aiImageFormErrors.style}
                  </p>
                )}
              </form>
            ) : (
              <TooltipProvider>
                <form id="template-refinement-form" onSubmit={handleTemplateRefinementSubmit} className="space-y-4">
                  <div className="space-y-3">
                    {/* 📝 Text & Colors Section */}
                    <Collapsible
                      open={openSection === "textColors"}
                      onOpenChange={(open) => setOpenSection(open ? "textColors" : null)}
                      className="rounded-lg border"
                    >
                      <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                        <span className="text-sm font-medium">📝 Text & Colors</span>
                        <ChevronDown
                          className={`text-muted-foreground h-4 w-4 transition-transform duration-200 ${
                            openSection === "textColors" ? "rotate-180" : ""
                          }`}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label htmlFor="template-title">Title</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="text-muted-foreground h-4 w-4 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[200px] text-wrap">
                                  <p>
                                    The main heading displayed on your social preview. Keep it concise and compelling.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <Input
                              id="template-title"
                              value={templateFormData.title}
                              onChange={(event) => setTemplateFormData("title", event.target.value)}
                              placeholder="Catchy headline that appears on the preview"
                            />
                          </div>

                          {social_image.page_type !== "ecommerce_product" &&
                            social_image.page_type !== "podcast_episode" &&
                            social_image.page_type !== "event_page" &&
                            social_image.page_type !== "blog_post" &&
                            social_image.page_type !== "book" && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor="template-description">Description</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <HelpCircle className="text-muted-foreground h-4 w-4 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[200px] text-pretty">
                                      <p>Secondary text that provides context. Up to 200 characters recommended.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Textarea
                                  id="template-description"
                                  value={templateFormData.description}
                                  onChange={(event) => setTemplateFormData("description", event.target.value)}
                                  placeholder="Supporting text that appears below the title (optional)"
                                  rows={2}
                                  maxLength={200}
                                />
                                <p className="text-muted-foreground text-xs">
                                  {templateFormData.description.length}/200 characters
                                </p>
                              </div>
                            )}

                          {/* Page-specific fields */}
                          {social_image.page_type === "ecommerce_product" && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="unique-selling-point">Unique Selling Point</Label>
                                <Textarea
                                  id="unique-selling-point"
                                  value={templateFormData.unique_selling_point}
                                  onChange={(event) => setTemplateFormData("unique_selling_point", event.target.value)}
                                  placeholder="What makes this product special?"
                                  rows={2}
                                />
                              </div>
                            </>
                          )}

                          {social_image.page_type === "podcast_episode" && (
                            <div className="space-y-2">
                              <Label htmlFor="episode-date">Episode Date</Label>
                              <Input
                                id="episode-date"
                                type="date"
                                value={templateFormData.episode_date}
                                onChange={(event) => setTemplateFormData("episode_date", event.target.value)}
                              />
                            </div>
                          )}

                          {social_image.page_type === "event_page" && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="event-date">Event Date</Label>
                                <Input
                                  id="event-date"
                                  type="date"
                                  value={templateFormData.event_date}
                                  onChange={(event) => setTemplateFormData("event_date", event.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="event-location">Event Location</Label>
                                <Input
                                  id="event-location"
                                  value={templateFormData.event_location}
                                  onChange={(event) => setTemplateFormData("event_location", event.target.value)}
                                  placeholder="City, Venue, or Online"
                                />
                              </div>
                            </>
                          )}

                          {social_image.page_type === "blog_post" && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="blog-author">Author</Label>
                                <Input
                                  id="blog-author"
                                  value={templateFormData.blog_author}
                                  onChange={(event) => setTemplateFormData("blog_author", event.target.value)}
                                  placeholder="Author name"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="blog-date">Publish Date</Label>
                                <Input
                                  id="blog-date"
                                  type="date"
                                  value={templateFormData.blog_date}
                                  onChange={(event) => setTemplateFormData("blog_date", event.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Author Avatar</Label>
                                <div className="relative">
                                  <Input
                                    id="blog-author-avatar-url"
                                    type="url"
                                    value={templateFormData.blog_author_avatar_url}
                                    onChange={(event) => {
                                      setTemplateFormData("blog_author_avatar_url", event.target.value);
                                      if (event.target.value.trim()) {
                                        setTemplateFormData("blog_author_avatar", null);
                                        setTemplateFormData("blog_author_avatar_name", "");
                                        setTemplateFormData("blog_author_avatar_preview", null);
                                      }
                                    }}
                                    onPaste={handleAuthorAvatarPaste}
                                    placeholder="Paste avatar or enter URL"
                                    className="pr-10"
                                  />
                                  <Clipboard className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                                </div>
                                <div className="relative">
                                  <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t" />
                                  </div>
                                  <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-background text-muted-foreground px-2">Or</span>
                                  </div>
                                </div>
                                <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors">
                                  <input
                                    type="file"
                                    onChange={handleAuthorAvatarUpload}
                                    accept="image/*"
                                    className="hidden"
                                    id="blog-author-avatar-upload"
                                  />
                                  <label htmlFor="blog-author-avatar-upload" className="block cursor-pointer">
                                    <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-1 h-6 w-6 transition-colors" />
                                    <p className="text-foreground text-sm font-medium">
                                      {templateFormData.blog_author_avatar_name
                                        ? templateFormData.blog_author_avatar_name
                                        : "Upload avatar"}
                                    </p>
                                  </label>
                                </div>

                                {(templateFormData.blog_author_avatar_name ||
                                  templateFormData.blog_author_avatar_url.trim()) && (
                                  <div className="space-y-2 rounded-lg border p-3">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm font-medium">New avatar</span>
                                      <button
                                        type="button"
                                        onClick={handleRemoveAuthorAvatar}
                                        className="text-muted-foreground hover:text-foreground rounded-full p-1"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                    {templateFormData.blog_author_avatar_preview && (
                                      <img
                                        src={templateFormData.blog_author_avatar_preview}
                                        alt="Avatar preview"
                                        className="h-12 w-12 rounded-full object-cover"
                                      />
                                    )}
                                    {templateFormData.blog_author_avatar_url.trim() &&
                                      !templateFormData.blog_author_avatar_preview && (
                                        <img
                                          src={templateFormData.blog_author_avatar_url.trim()}
                                          alt="Avatar preview"
                                          className="h-12 w-12 rounded-full object-cover"
                                        />
                                      )}
                                  </div>
                                )}

                                {showCurrentAuthorAvatar && (
                                  <div className="space-y-2 rounded-lg border p-3">
                                    <span className="text-sm font-medium">Current avatar</span>
                                    <img
                                      src={currentBlogAuthorAvatarUrl}
                                      alt="Current avatar"
                                      className="h-12 w-12 rounded-full object-cover"
                                    />
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {social_image.page_type === "book" && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="book-author">Author</Label>
                                <Input
                                  id="book-author"
                                  value={templateFormData.book_author}
                                  onChange={(event) => setTemplateFormData("book_author", event.target.value)}
                                  placeholder="Author name"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="book-series-number">Series Number</Label>
                                <Input
                                  id="book-series-number"
                                  type="number"
                                  min={0}
                                  value={templateFormData.book_series_number ?? ""}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setTemplateFormData(
                                      "book_series_number",
                                      value === "" ? null : parseInt(value, 10),
                                    );
                                  }}
                                  placeholder="Book number in series"
                                />
                              </div>
                            </>
                          )}

                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label>Colors</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="text-muted-foreground h-4 w-4 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[200px] text-pretty">
                                  <p>Background and text colors should have sufficient contrast for readability.</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="flex gap-4">
                              <div className="flex-1 space-y-2">
                                <label htmlFor="bg-color" className="text-muted-foreground text-sm">
                                  Background
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="color"
                                    id="bg-color"
                                    value={templateFormData.background_color}
                                    onChange={(event) => setTemplateFormData("background_color", event.target.value)}
                                    className="border-border h-10 w-12 cursor-pointer rounded border"
                                  />
                                  <span className="text-muted-foreground font-mono text-sm">
                                    {templateFormData.background_color}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 space-y-2">
                                <label htmlFor="text-color" className="text-muted-foreground text-sm">
                                  Text
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="color"
                                    id="text-color"
                                    value={templateFormData.text_color}
                                    onChange={(event) => setTemplateFormData("text_color", event.target.value)}
                                    className="border-border h-10 w-12 cursor-pointer rounded border"
                                  />
                                  <span className="text-muted-foreground font-mono text-sm">
                                    {templateFormData.text_color}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* 🏷️ Logo Section */}
                    <Collapsible
                      open={openSection === "logo"}
                      onOpenChange={(open) => setOpenSection(open ? "logo" : null)}
                      className="rounded-lg border"
                    >
                      <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                        <span className="text-sm font-medium">🏷️ Logo</span>
                        <ChevronDown
                          className={`text-muted-foreground h-4 w-4 transition-transform duration-200 ${
                            openSection === "logo" ? "rotate-180" : ""
                          }`}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                        <div className="space-y-2">
                          <p className="text-muted-foreground text-xs">
                            Update your social image&apos;s brand logo. Recommended: PNG with transparent background.
                          </p>
                          <div className="space-y-3">
                            {showCurrentLogo && (
                              <div className="space-y-2 rounded-lg border border-dashed p-3">
                                <p className="text-muted-foreground text-xs">Current logo</p>
                                <div className="relative aspect-square max-w-[200px] overflow-hidden rounded-lg border">
                                  <img
                                    src={currentLogoUrl}
                                    alt="Current logo"
                                    className="h-full w-full object-contain"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={handleRemoveCurrentLogo}
                                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs font-medium"
                                >
                                  <X className="h-3.5 w-3.5" />
                                  Remove current logo
                                </button>
                              </div>
                            )}
                            <div className="relative">
                              <Input
                                id="custom-logo-url"
                                type="url"
                                value={templateFormData.custom_logo_url}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setTemplateFormData("custom_logo_url", value);
                                  if (value.trim()) {
                                    setTemplateFormData("custom_logo", null);
                                    setTemplateFormData("custom_logo_name", "");
                                    setTemplateFormData("custom_logo_preview", null);
                                    setTemplateFormData("remove_custom_logo", false);
                                  }
                                }}
                                onPaste={handleLogoPaste}
                                placeholder="Paste image or enter URL"
                                className="pr-10"
                              />
                              <Clipboard className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                            </div>
                            <div className="relative">
                              <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                              </div>
                              <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background text-muted-foreground px-2">Or</span>
                              </div>
                            </div>
                            <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors">
                              <input
                                type="file"
                                onChange={handleLogoUpload}
                                accept="image/*"
                                className="hidden"
                                id="custom-logo-upload"
                              />
                              <label htmlFor="custom-logo-upload" className="block cursor-pointer">
                                <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-2 h-8 w-8 transition-colors" />
                                <p className="text-foreground text-sm font-medium">
                                  {templateFormData.custom_logo_name
                                    ? templateFormData.custom_logo_name
                                    : "Click to upload"}
                                </p>
                                <p className="text-muted-foreground text-xs">PNG, JPG up to 10MB</p>
                              </label>
                            </div>
                          </div>
                          {(templateFormData.custom_logo_name || templateFormData.custom_logo_url.trim()) && (
                            <div className="space-y-2">
                              {templateFormData.custom_logo_preview && (
                                <div className="relative aspect-square max-w-[200px] overflow-hidden rounded-lg border">
                                  <img
                                    src={templateFormData.custom_logo_preview}
                                    alt="Custom logo preview"
                                    className="h-full w-full object-contain"
                                  />
                                </div>
                              )}
                              {templateFormData.custom_logo_url.trim() && !templateFormData.custom_logo_preview && (
                                <div className="relative aspect-square max-w-[200px] overflow-hidden rounded-lg border">
                                  <img
                                    src={templateFormData.custom_logo_url.trim()}
                                    alt="Custom logo preview"
                                    className="h-full w-full object-contain"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                </div>
                              )}
                              <div className="bg-secondary/50 flex items-center justify-between gap-2 rounded-lg p-3">
                                <span className="text-foreground truncate text-sm">
                                  {templateFormData.custom_logo_name || templateFormData.custom_logo_url.trim()}
                                </span>
                                <button
                                  type="button"
                                  onClick={handleRemoveLogo}
                                  className="hover:bg-secondary rounded p-1 transition-colors"
                                >
                                  <X className="text-muted-foreground h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* 🖼️ Custom Image Section */}
                    <Collapsible
                      open={openSection === "image"}
                      onOpenChange={(open) => setOpenSection(open ? "image" : null)}
                      className="rounded-lg border"
                    >
                      <CollapsibleTrigger className="hover:bg-muted/50 flex w-full items-center justify-between px-4 py-3 transition-colors">
                        <span className="text-sm font-medium">🖼️ Custom Image</span>
                        <ChevronDown
                          className={`text-muted-foreground h-4 w-4 transition-transform duration-200 ${
                            openSection === "image" ? "rotate-180" : ""
                          }`}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="border-t px-4 pt-3 pb-4">
                        <div className="space-y-2">
                          <p className="text-muted-foreground text-xs">
                            Showcase a custom image on your social preview. Recommended size: 600x600px.
                          </p>
                          <div className="space-y-3">
                            {showCurrentImage && (
                              <div className="space-y-2 rounded-lg border border-dashed p-3">
                                <p className="text-muted-foreground text-xs">Current image</p>
                                <div className="relative aspect-[600/630] overflow-hidden rounded-lg border">
                                  <img
                                    src={currentCustomImageUrl}
                                    alt="Current custom image"
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={handleRemoveCurrentImage}
                                  className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs font-medium"
                                >
                                  <X className="h-3.5 w-3.5" />
                                  Remove current image
                                </button>
                              </div>
                            )}
                            <div className="relative">
                              <Input
                                id="custom-image-url"
                                type="url"
                                value={templateFormData.custom_image_url}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setTemplateFormData("custom_image_url", value);
                                  if (value.trim()) {
                                    setTemplateFormData("custom_image", null);
                                    setTemplateFormData("custom_image_name", "");
                                    setTemplateFormData("custom_image_preview", null);
                                    setTemplateFormData("remove_custom_image", false);
                                  }
                                }}
                                onPaste={handleImagePaste}
                                placeholder="Paste image or enter URL"
                                className="pr-10"
                              />
                              <Clipboard className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2" />
                            </div>
                            <div className="relative">
                              <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                              </div>
                              <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-background text-muted-foreground px-2">Or</span>
                              </div>
                            </div>
                            <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors">
                              <input
                                type="file"
                                onChange={handleImageUpload}
                                accept="image/*"
                                className="hidden"
                                id="custom-image-upload"
                              />
                              <label htmlFor="custom-image-upload" className="block cursor-pointer">
                                <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-2 h-8 w-8 transition-colors" />
                                <p className="text-foreground text-sm font-medium">
                                  {templateFormData.custom_image_name
                                    ? templateFormData.custom_image_name
                                    : "Click to upload"}
                                </p>
                                <p className="text-muted-foreground text-xs">PNG, JPG up to 10MB</p>
                              </label>
                            </div>
                          </div>
                          {(templateFormData.custom_image_name || templateFormData.custom_image_url.trim()) && (
                            <div className="space-y-2">
                              {templateFormData.custom_image_preview && (
                                <div className="relative aspect-[600/630] overflow-hidden rounded-lg border">
                                  <img
                                    src={templateFormData.custom_image_preview}
                                    alt="Custom image preview"
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              )}
                              {templateFormData.custom_image_url.trim() && !templateFormData.custom_image_preview && (
                                <div className="relative aspect-[600/630] overflow-hidden rounded-lg border">
                                  <img
                                    src={templateFormData.custom_image_url.trim()}
                                    alt="Custom image preview"
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                </div>
                              )}
                              <div className="bg-secondary/50 flex items-center justify-between gap-2 rounded-lg p-3">
                                <span className="text-foreground truncate text-sm">
                                  {templateFormData.custom_image_name || templateFormData.custom_image_url.trim()}
                                </span>
                                <button
                                  type="button"
                                  onClick={handleRemoveImage}
                                  className="hover:bg-secondary rounded p-1 transition-colors"
                                >
                                  <X className="text-muted-foreground h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </form>
              </TooltipProvider>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleDialogChange(false)}>
              Cancel
            </Button>
            {selectedImage?.is_ai_generated ? (
              <Button
                type="submit"
                form="ai-refinement-form"
                disabled={processingAiImageFormData || !aiImageFormData.image_url}
              >
                {processingAiImageFormData ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Refining
                  </>
                ) : (
                  "Generate 2 more"
                )}
              </Button>
            ) : (
              <Button type="submit" form="template-refinement-form" disabled={processingTemplateRefinement}>
                {processingTemplateRefinement ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating
                  </>
                ) : (
                  "Generate new preview"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
